/// <amd-module name="Test/TestPlansHubRefresh/AuthorPivotMenuService/AuthorPivotMenuProviderService" />
import { IAuthorPivotContextMenuParams } from "Test/TestPlansHubRefresh/AuthorPivotMenuService/AuthorPivotMenuService.types";
import * as Resources from "Test/TestPlansHubRefresh/AuthorPivotMenuService/Resources";
import { AuthorActions } from "Test/TestPlansHubRefresh/Pivots/AuthorPivot/Redux/AuthorActions";
import { TestSuiteType } from "TestPlanCommon/Contracts/TestPlan";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { IMenuService } from "VSS/Features/PlatformUI/ContributedMenu";
import { IVssPageContext, Services, VssService } from "VSS/Platform/Context";
import { Components } from "VSS/Platform/Layout";
import { Dialog } from "VSSUI/Dialog";
import { IMenuItem, MenuItemType } from "VSSUI/Menu";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { TestHubRefreshConstants } from "Test/TestPlansHubRefresh/TestHub/Constants";
import { SuitesActions } from "Test/TestPlansHubRefresh/TestHub/Redux/Suites/SuitesActions";

// This class is a provider service class to get the context menu options for test case item(s).
export class AuthorPivotMenuProviderService extends VssService implements IMenuService {
    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);
        this.pageContext = pageContext;
    }

    public _serviceRestart(pageContext: IVssPageContext): void {
        super._serviceRestart(pageContext);
    }

    public removeTestCasesFromSuite(params: IAuthorPivotContextMenuParams): void {
        const testCaseIds: number[] = this._getTestCaseIds(params);
        const suiteId: number = params.suiteId;
        params.dispatch(AuthorActions.removeTestCasesFromSuite(params.planId, suiteId, testCaseIds, params.selection));
    }

    public assignConfigurationToTestCases(params: IAuthorPivotContextMenuParams): void {
        const testCaseIds: number[] = this._getTestCaseIds(params);
        params.dispatch(AuthorActions.assignConfigurationToTestCases(params.planId, params.suiteId, testCaseIds));
    }

    public editTestCasesUsingGrid(params: IAuthorPivotContextMenuParams): void {
        const testCaseIds: number[] = this._getTestCaseIds(params);
        params.dispatch(
            AuthorActions.addOrEditTestCasesUsingGrid(
                testCaseIds,
                params.planId,
                params.suiteId,
                params.planName,
                params.areaPath,
                params.iterationPath
            )
        );
    }

    public openTestCase(params: IAuthorPivotContextMenuParams): void {
        if (params.selection.value.length == 1) {
            const testCaseIndex = params.selection.value[0].beginIndex;
            const testCaseId: number = params.testCases[testCaseIndex].id;
            const testCaseTitle: string = params.testCases[testCaseIndex].name;
            const testCaseState: string = params.testCases[testCaseIndex].state;

            let testCaseAssignedTo = "";
            if (params.testCases[testCaseIndex].assignedTo && params.testCases[testCaseIndex].assignedTo.displayName) {
                testCaseAssignedTo = params.testCases[testCaseIndex].assignedTo.displayName;
            }

            params.dispatch(
                AuthorActions.openExistingTestCaseWitForm(params.planId, params.suiteId, testCaseId, testCaseTitle, testCaseState, testCaseAssignedTo)
            );
        }
    }

    public getMenuItems(menuId: string, props: IAuthorPivotContextMenuParams): IMenuItem[] | null {
        const menuItems: IMenuItem[] = [];
        if (menuId === "ms.vss-test-web.test-case-context-menu") {
            if (props.selection.selectedCount === 1) {
                menuItems.push({
                    id: "view-linked-items",
                    text: Resources.ViewLinkedItems,
                    iconProps: { iconName: "Link", className: "link-icon" },
                    onActivate: (menuItem: IMenuItem) => {
                        if (props.linkedItemsPanelOpen) {
                            props.linkedItemsPanelOpen();
                        }
                    },
                    data: props
                });

                menuItems.push({
                    id: "separator",
                    itemType: MenuItemType.Divider
                });

                menuItems.push({
                    id: "open-test-case",
                    iconProps: { iconName: "FullScreen", className: "open-icon" },
                    text: Resources.OpenTestCase,
                    onActivate: (menuItem: IMenuItem) => {
                        this.openTestCase(props);
                    },
                    data: props
                });
            }


            if (props.suiteType !== TestSuiteType.DynamicTestSuite) {
                menuItems.push({
                    id: "remove-test-case",
                    iconProps: { iconName: "Delete", className: "remove-icon" },
                    text: Resources.Remove,
                    onActivate: (menuItem: IMenuItem) => {
                        this.showConfirmationDialog(props);
                    },
                    data: props
                });
                menuItems.push({
                    id: "edit-test-case-using-grid",
                    iconProps: { iconName: "Edit", className: "edit-icon" },
                    text: Resources.EditTestCasesUsingGrid,
                    onActivate: (menuItem: IMenuItem) => {
                        this.editTestCasesUsingGrid(props);
                    },
                    data: props
                });
            }

            menuItems.push({
                id: "edit-test-cases",
                iconProps: { iconName: "Edit", className: "edit-icon" },
                text: Resources.EditTestCases,
                onActivate: (menuItem: IMenuItem) => {
                    const testCaseIds: number[] = [];
                    const selectionLength = props.selection.value.length;
                    for (let i = 0; i < selectionLength; i++) {
                        for (let j = props.selection.value[i].beginIndex; j <= props.selection.value[i].endIndex; j++) {
                            testCaseIds.push(props.testCases[j].id);
                        }
                    }
                    props.dispatch(AuthorActions.bulkEditTestCases(props.planId, props.suiteId, testCaseIds));
                },
                data: props
            });

            // newly moved assign configurations to test cases 
            menuItems.push({
                id: "test-case-assign-configuration",
                iconProps: { iconName: "CheckList", className: "checklist-icon" },
                text: Resources.AssignConfiguration,
                onActivate: (menuItem: IMenuItem) => {
                    this.assignConfigurationToTestCases(props);
                },
                data: props
            });

            menuItems.push({
                id: "copy-test-cases",
                text: Resources.CopyTestCases,
                iconProps: { iconName: "Copy", className: "copy-icon" },
                onActivate: (menuItem: IMenuItem) => {
                    if (props.cloneTestCasesPanelOpen) {
                        props.cloneTestCasesPanelOpen();
                    }
                },
                data: props
            });

            
            menuItems.push({
                id: "export-test-cases",
                text: Resources.ExportTestCases,
                iconProps: { iconName: "Download", className: "download-icon" },
                onActivate: (menuItem: IMenuItem) => {
                    this._exportTestCases(props);
                },
                data: props
            });

            menuItems.push({
                id: "export-test-cases-xlsx",
                text: Resources.ExportTestCasesToXLSX,
                iconProps: { iconName: "Download", className: "download-icon" },
                onActivate: (menuItem: IMenuItem) => {
                    this._exportTestCasesToXLSX(props);
                },
                data: props
            });
        }
        return menuItems;
    }

    private _exportTestCases(params: IAuthorPivotContextMenuParams): void {
        const testCaseIds: number[] = this._getTestCaseIds(params);
        params.dispatch(SuitesActions.exportTestCasesToCSV(params.planId, params.suiteId, params.planName, params.suiteName, testCaseIds));
    }

    private _exportTestCasesToXLSX(params: IAuthorPivotContextMenuParams): void {
        const testCaseIds: number[] = this._getTestCaseIds(params);
        params.dispatch(SuitesActions.exportTestCasesToExcel(params.planId, params.suiteId, params.planName, params.suiteName, testCaseIds));
    }

    private _getTestCaseIds(params: IAuthorPivotContextMenuParams) {
        const testCaseIds: number[] = [];

        const selectionLength = params.selection.value.length;
        for (let i = 0; i < selectionLength; i++) {
            for (let j = params.selection.value[i].beginIndex; j <= params.selection.value[i].endIndex; j++) {
                if (params.testCases[j]) {
                    testCaseIds.push(params.testCases[j].id);
                }
            }
        }
        return testCaseIds;
    }

    private showConfirmationDialog(props: IAuthorPivotContextMenuParams) {
        const globalMessageService = this.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
        globalMessageService.addDialog(
            {
                componentType: "remove-test-case-confirmation-dialog",
                contentDependencies: ["ms.vss-features.ui-layer-content"],
                contentProperties: {
                    children: Resources.TestCasesRemoveConfirmation,
                    titleProps: { text: Resources.RemoveTestCasesFromSuite },
                    footerButtonProps: [
                        {
                            text: Resources.Cancel,
                            onClick: () => {
                                globalMessageService.closeDialog();
                            }
                        },
                        {
                            text: Resources.Confirm,
                            onClick: () => {
                                this.removeTestCasesFromSuite(props);
                                globalMessageService.closeDialog();
                            },
                            primary: true
                        }
                    ]
                }
            },
            true,
            true
        );
    }
}
Components.add("remove-test-case-confirmation-dialog", Dialog);
Services.add("author-pivot-menu-provider", { serviceFactory: AuthorPivotMenuProviderService });
